import { v4 as uuidv4 } from 'uuid';

/**
 * Package Mapping Entity
 * 
 * Domain entity representing the mapping of a package to a strategic domain.
 * Encapsulates package classification, confidence scoring, and mapping status.
 */
export class PackageMapping {
    private constructor(
        private readonly _id: string,
        private readonly _packageName: string,
        private readonly _packageType: PackageType,
        private _domainId: string | null,
        private _classification: PackageClassification,
        private _status: MappingStatus,
        private readonly _createdAt: Date,
        private _updatedAt: Date,
        private _lastReviewedBy: string | null = null,
        private _notes: string | null = null
    ) {}

    // Factory method for creating new package mappings
    static create(
        packageName: string,
        packageType: PackageType,
        domainId?: string,
        classification?: PackageClassification
    ): PackageMapping {
        const now = new Date();
        const defaultClassification = classification || {
            confidence: 0.5,
            reasoning: 'Manual classification',
            suggestedDomain: domainId || null,
            automaticallyClassified: false
        };

        return new PackageMapping(
            uuidv4(),
            packageName,
            packageType,
            domainId || null,
            defaultClassification,
            domainId ? MappingStatus.ALIGNED : MappingStatus.ORPHANED,
            now,
            now
        );
    }

    // Factory method for reconstituting from persistence
    static reconstitute(
        id: string,
        packageName: string,
        packageType: PackageType,
        domainId: string | null,
        classification: PackageClassification,
        status: MappingStatus,
        createdAt: Date,
        updatedAt: Date,
        lastReviewedBy?: string,
        notes?: string
    ): PackageMapping {
        return new PackageMapping(
            id,
            packageName,
            packageType,
            domainId,
            classification,
            status,
            createdAt,
            updatedAt,
            lastReviewedBy || null,
            notes || null
        );
    }

    // Getters
    get id(): string { return this._id; }
    get packageName(): string { return this._packageName; }
    get packageType(): PackageType { return this._packageType; }
    get domainId(): string | null { return this._domainId; }
    get classification(): PackageClassification { return this._classification; }
    get status(): MappingStatus { return this._status; }
    get createdAt(): Date { return this._createdAt; }
    get updatedAt(): Date { return this._updatedAt; }
    get lastReviewedBy(): string | null { return this._lastReviewedBy; }
    get notes(): string | null { return this._notes; }

    // Business methods
    assignToDomain(domainId: string, reviewedBy?: string): void {
        this._domainId = domainId;
        this._status = MappingStatus.ALIGNED;
        this._lastReviewedBy = reviewedBy || null;
        this._updatedAt = new Date();
    }

    markAsOrphaned(): void {
        this._domainId = null;
        this._status = MappingStatus.ORPHANED;
        this._updatedAt = new Date();
    }

    markForReview(reason?: string): void {
        this._status = MappingStatus.NEEDS_REVIEW;
        if (reason) {
            this._notes = reason;
        }
        this._updatedAt = new Date();
    }

    markAsDisputed(reason: string): void {
        this._status = MappingStatus.DISPUTED;
        this._notes = reason;
        this._updatedAt = new Date();
    }

    updateClassification(classification: PackageClassification): void {
        this._classification = classification;
        this._updatedAt = new Date();
    }

    addNotes(notes: string, reviewedBy?: string): void {
        this._notes = notes;
        this._lastReviewedBy = reviewedBy || this._lastReviewedBy;
        this._updatedAt = new Date();
    }

    // Domain validation
    isWellAligned(): boolean {
        return this._status === MappingStatus.ALIGNED && 
               this._domainId !== null && 
               this._classification.confidence > 0.7;
    }

    needsAttention(): boolean {
        return this._status === MappingStatus.ORPHANED || 
               this._status === MappingStatus.NEEDS_REVIEW ||
               this._status === MappingStatus.DISPUTED ||
               this._classification.confidence < 0.6;
    }
}

export enum PackageType {
    DOMAIN = 'domain',
    API = 'api', 
    SERVICE = 'service',
    APP = 'app',
    SHARED = 'shared',
    INFRASTRUCTURE = 'infrastructure'
}

export enum MappingStatus {
    ALIGNED = 'aligned',
    ORPHANED = 'orphaned',
    NEEDS_REVIEW = 'needs_review',
    DISPUTED = 'disputed'
}

export interface PackageClassification {
    confidence: number; // 0.0 to 1.0
    reasoning: string;
    suggestedDomain?: string | null;
    automaticallyClassified: boolean;
}

export interface PackageAnalysis {
    packageName: string;
    suggestedDomains: Array<{
        domainId: string;
        domainName: string;
        confidence: number;
        reasoning: string;
    }>;
    packageType: PackageType;
    complexity: 'low' | 'medium' | 'high';
    recommendations: string[];
}